/**
 * The command palette's matcher.
 *
 * Subsequence matching with three bonuses, which is the smallest thing that
 * behaves the way people expect: "flat" should find "Fix the flaky auth test",
 * and "revq" should find "Review queue" ahead of "Requeue". The bonuses are
 * word-start (a letter after a space, dash, dot, slash or underscore), an
 * unbroken run, and a match at position zero. A miss returns `null` rather than
 * a sentinel score so callers filter on truthiness and never sort junk.
 *
 * A match also says *how* it matched, because the two kinds are not comparable.
 * A haystack that contains the query as it was typed answered the question; one
 * that merely has those letters lying around in order is a guess, and once any
 * row has answered, the guesses are noise. That is what used to put a draft task
 * in the results for "failed".
 *
 * There is no dependency here on purpose: a fuzzy library is 8 KB of bundle for
 * forty lines of logic, and the hub serves every byte of this page itself.
 */

const BOUNDARY = new Set([' ', '-', '_', '.', '/', ':', '·', '(', '[']);

/**
 * Below this many characters a subsequence is a coincidence rather than a
 * search: three letters lying around in unrelated words match most of the fleet,
 * which is how "T-7" used to return rows with no T-7 in them. A short query has
 * to appear as it was typed, at a word boundary or anywhere inside one.
 */
const SHORT_QUERY = 4;

export interface FuzzyMatch {
  score: number;
  /** The query appears as typed, rather than as letters found in order. */
  direct: boolean;
}

export function fuzzyMatch(haystack: string, needle: string): FuzzyMatch | null {
  if (!needle) return { score: 0, direct: true };
  const target = haystack.toLowerCase();
  const query = needle.toLowerCase();

  // An exact substring is always the best answer, scored by how early it lands,
  // how much of the string it covers, and whether it starts a word.
  const at = target.indexOf(query);
  if (at !== -1) {
    const boundary = at === 0 || BOUNDARY.has(target[at - 1] as string);
    return {
      score: 1000 - at * 4 + Math.round((query.length / target.length) * 60) + (boundary ? 30 : 0),
      direct: true,
    };
  }

  if (query.length < SHORT_QUERY) return null;

  let score = 0;
  let cursor = 0;
  let run = 0;

  for (const character of query) {
    let found = -1;
    for (let index = cursor; index < target.length; index++) {
      if (target[index] === character) {
        found = index;
        break;
      }
    }
    if (found === -1) return null;

    const atStart = found === 0;
    const afterBoundary = found > 0 && BOUNDARY.has(target[found - 1] as string);
    score += 12;
    if (atStart) score += 22;
    else if (afterBoundary) score += 16;
    if (found === cursor && cursor > 0) {
      run += 1;
      score += 6 + run * 2;
    } else {
      run = 0;
      score -= Math.min(10, found - cursor);
    }
    cursor = found + 1;
  }

  // Short targets that matched everything are better answers than long ones.
  return { score: score + Math.max(0, 40 - target.length), direct: false };
}
